/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Partner_Partner as EmbeddedPartner } from '__generated__/graphql-types-do-not-use';

import { Link, Typography2 } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import PartnerSquareLogo from 'bundles/unified-description-page-common/components/shared/PartnerSquareLogo';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { getPartnerPageUrl } from 'bundles/unified-description-page-common/utils';

const styles = {
  container: css`
    display: flex;
    align-items: center;
    margin-bottom: var(--cds-spacing-100);
  `,
  partnerName: css`
    color: var(--cds-color-neutral-primary) !important;
    margin-left: var(--cds-spacing-100) !important;
  `,
};

type Props = {
  partner: EmbeddedPartner;
  disableCTA?: boolean;
  trackingName?: string;
  trackingLocation?: SectionName;
};

const EmbeddedContentPartner = ({ partner, trackingName, disableCTA, trackingLocation }: Props) => {
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  const styledPartnerName = (
    <Typography2 component="span" variant="bodyPrimary">
      {partner.name}
    </Typography2>
  );

  const partnerPageUrl = getPartnerPageUrl(partner.slug);
  const onClickToTrack = (linkName: ButtonName, linkURL: string) => () => {
    track('click_button', {
      button: { name: linkName, linkURL },
      ...(trackingLocation
        ? {
            pageSection: {
              sectionName: trackingLocation,
            },
          }
        : {}),
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container}>
      {partner.squareLogo && <PartnerSquareLogo src={partner.squareLogo} width={36} borderRadius={4} />}
      {!disableCTA ? (
        <Link
          component={TrackedLink2}
          href={partnerPageUrl}
          trackingName={trackingName}
          css={styles.partnerName}
          onClick={onClickToTrack('description_page_embedded_partner', partnerPageUrl)}
        >
          {styledPartnerName}
        </Link>
      ) : (
        styledPartnerName
      )}
    </div>
  );
};

export default EmbeddedContentPartner;
